import React, { useState, useEffect } from 'react';
import { API_BASE } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Activity, TrendingUp, LogOut, ClipboardCheck, AlertCircle, FileText, CreditCard, Shield, Heart, Crown, ChevronRight, Quote, Sparkles, HelpCircle, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';
import { getQuoteOfTheDay } from '../utils/motivationalQuotes';

const Dashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [todayWellness, setTodayWellness] = useState(null);
  const [latestCTL, setLatestCTL] = useState(null);
  const [ctlHistory, setCTLHistory] = useState([]);
  const [ctlCanSubmit, setCTLCanSubmit] = useState(null);
  const [monthlyFeedback, setMonthlyFeedback] = useState(null);
  const [protocolBenefit, setProtocolBenefit] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [ctlDialogOpen, setCTLDialogOpen] = useState(false);
  const [ctlValue, setCTLValue] = useState('');
  const [fadigaValue, setFadigaValue] = useState('');
  const [condicionamentoValue, setCondicionamentoValue] = useState('');
  const [formaValue, setFormaValue] = useState('');
  const [ctlNotes, setCTLNotes] = useState('');
  const [submittingCTL, setSubmittingCTL] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Use allSettled so individual endpoint failures don't block the entire dashboard
      // (previous Promise.all behavior caused CTL submissions to appear "not updated"
      // whenever any sibling endpoint - e.g. monthly-feedback - returned an error).
      const results = await Promise.allSettled([
        axios.get(`${API_BASE}/api/wellness/today`),
        axios.get(`${API_BASE}/api/performance/ctl/latest`),
        axios.get(`${API_BASE}/api/performance/feedback`),
        axios.get(`${API_BASE}/api/payment/info`),
        axios.get(`${API_BASE}/api/performance/ctl/history?limit=120`),
        axios.get(`${API_BASE}/api/performance/ctl/can-submit`),
        axios.get(`${API_BASE}/api/performance/monthly-feedback`),
        axios.get(`${API_BASE}/api/performance/protocol-benefit`),
      ]);

      const getData = (idx, fallback = null) =>
        results[idx].status === 'fulfilled' ? results[idx].value.data : fallback;

      setTodayWellness(getData(0));
      setLatestCTL(getData(1));
      setFeedback(getData(2));
      setPaymentInfo(getData(3));
      setCTLHistory(getData(4, []));
      setCTLCanSubmit(getData(5));
      setMonthlyFeedback(getData(6));
      setProtocolBenefit(getData(7));

      // Log any failed endpoint so we can diagnose if it keeps breaking
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['wellness/today', 'ctl/latest', 'performance/feedback', 'payment/info', 'ctl/history', 'ctl/can-submit', 'monthly-feedback', 'protocol-benefit'];
          // eslint-disable-next-line no-console
          console.warn(`Dashboard: endpoint ${names[i]} failed`, r.reason?.response?.status, r.reason?.message);
        }
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async (newPlan) => {
    setChangingPlan(true);
    try {
      const { data } = await axios.put(
        `${API_BASE}/api/auth/profile`,
        { plan: newPlan }
      );
      updateUser({ plan: data.plan });
      toast.success(`Plano alterado para ${newPlan === 'ouro' ? 'Ouro' : 'Bronze'} com sucesso!`);
      setPlanDialogOpen(false);
      loadDashboardData();
    } catch (error) {
      toast.error('Erro ao alterar plano');
    } finally {
      setChangingPlan(false);
    }
  };

  const handleCTLSubmit = async (e) => {
    e.preventDefault();
    setSubmittingCTL(true);

    try {
      await axios.post(
        `${API_BASE}/api/performance/ctl`,
        {
          ctl_value: parseFloat(condicionamentoValue),
          fadiga: fadigaValue ? parseFloat(fadigaValue) : null,
          condicionamento: parseFloat(condicionamentoValue),
          forma: formaValue ? parseFloat(formaValue) : null,
          notes: null
        }
      );

      toast.success('Métricas registradas com sucesso!');
      setCTLDialogOpen(false);
      setFadigaValue('');
      setCondicionamentoValue('');
      setFormaValue('');
      loadDashboardData();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data?.detail || 'Você já registrou o CTL deste ciclo mensal.');
      } else {
        toast.error('Erro ao registrar. Tente novamente.');
      }
    } finally {
      setSubmittingCTL(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#39FF14';
    if (score >= 60) return '#00F0FF';
    if (score >= 40) return '#FFB800';
    return '#FF3366';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#00F0FF] text-xl font-bold tracking-wider uppercase">Carregando...</div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';
  const isOuro = user?.plan === 'ouro';

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative">
      {/* Futuristic metric background */}
      <div 
        className="fixed inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d0c3862c-91cf-4abe-86c7-d1852ab015aa/images/79f43b2c808bf03b49df74617e42043ce286507138776852627882c75eb6dc21.png)',
          backgroundSize: 'cover'
        }}
      ></div>

      <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <NavArrows />
            <TCSLogo size="sm" showTagline={false} />
            <div className="hidden md:block">
              <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">
                {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <p className="text-xs text-[#A1A1AA]">
                {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>

          {/* User Profile with Status */}
          <div className="flex items-center gap-3 flex-1 justify-end">
            {/* Daily Status Badge */}
            {todayWellness?.daily_status && (
              <div 
                className="hidden md:flex items-center gap-2 px-3 py-2 border-2"
                style={{ 
                  borderColor: todayWellness.daily_status.color,
                  backgroundColor: `${todayWellness.daily_status.color}15`
                }}
                data-testid="daily-status-badge"
              >
                <div 
                  className="w-3 h-3 animate-pulse"
                  style={{ backgroundColor: todayWellness.daily_status.color }}
                ></div>
                <span 
                  className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: todayWellness.daily_status.color }}
                >
                  {todayWellness.daily_status.level}
                </span>
              </div>
            )}

            {/* User Avatar + Name */}
            <div className="flex items-center gap-2">
              {user?.photo ? (
                <img 
                  src={user.photo} 
                  alt={user.name}
                  className="w-10 h-10 border-2 border-[#00F0FF] object-cover"
                  data-testid="user-avatar"
                />
              ) : (
                <div className="w-10 h-10 border-2 border-[#00F0FF] bg-[#121212] flex items-center justify-center">
                  <span className="text-[#00F0FF] font-bold">{user?.name?.[0] || '?'}</span>
                </div>
              )}
              <div className="text-right hidden lg:block">
                <p className="text-sm text-white font-semibold truncate max-w-[150px]">{user?.name}</p>
                <p className="text-xs text-[#A1A1AA] truncate max-w-[150px]">{user?.email}</p>
              </div>
            </div>

            <Button
              onClick={() => navigate('/protocol')}
              variant="outline"
              className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none hidden xl:inline-flex"
              size="sm"
              data-testid="nav-protocol-button"
            >
              <FileText className="h-4 w-4 mr-2" />
              Protocolo
            </Button>
            <Button
              onClick={() => navigate('/payment')}
              variant="outline"
              className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none hidden xl:inline-flex"
              size="sm"
              data-testid="nav-payment-button"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Mensalidade
            </Button>
            <Button
              onClick={() => navigate('/channel')}
              variant="outline"
              className="bg-transparent border-[#FF0000] text-[#FF0000] hover:bg-[#FF0000]/10 rounded-none hidden xl:inline-flex"
              size="sm"
              data-testid="nav-channel-button"
            >
              <Youtube className="h-4 w-4 mr-2" />
              Canal YT
            </Button>
            <Button
              onClick={() => navigate('/help')}
              variant="outline"
              className="bg-transparent border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none hidden xl:inline-flex"
              size="sm"
              data-testid="nav-help-button"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Ajuda
            </Button>
            {isAdmin && (
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="bg-transparent border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10 rounded-none hidden xl:inline-flex"
                size="sm"
                data-testid="nav-admin-button"
              >
                <Shield className="h-4 w-4 mr-2" />
                Admin
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="outline"
              size="icon"
              className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none"
              data-testid="dashboard-logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Mobile Status Bar */}
        {todayWellness?.daily_status && (
          <div 
            className="md:hidden flex items-center justify-center gap-2 py-2 border-t"
            style={{ 
              borderColor: todayWellness.daily_status.color,
              backgroundColor: `${todayWellness.daily_status.color}15`
            }}
          >
            <div 
              className="w-2 h-2 animate-pulse"
              style={{ backgroundColor: todayWellness.daily_status.color }}
            ></div>
            <span 
              className="text-xs font-bold uppercase tracking-wider"
              style={{ color: todayWellness.daily_status.color }}
            >
              Status: {todayWellness.daily_status.level}
            </span>
          </div>
        )}

        {/* Mobile Navigation */}
        <div className="md:hidden flex gap-1 px-4 pb-3 overflow-x-auto">
          <Button
            onClick={() => navigate('/protocol')}
            size="sm"
            variant="outline"
            className="bg-transparent border-[#00F0FF] text-[#00F0FF] rounded-none text-xs flex-shrink-0"
          >
            <FileText className="h-3 w-3 mr-1" />
            Protocolo
          </Button>
          <Button
            onClick={() => navigate('/payment')}
            size="sm"
            variant="outline"
            className="bg-transparent border-[#00F0FF] text-[#00F0FF] rounded-none text-xs flex-shrink-0"
          >
            <CreditCard className="h-3 w-3 mr-1" />
            Mensalidade
          </Button>
          <Button
            onClick={() => navigate('/channel')}
            size="sm"
            variant="outline"
            className="bg-transparent border-[#FF0000] text-[#FF0000] rounded-none text-xs flex-shrink-0"
            data-testid="nav-channel-button-mobile"
          >
            <Youtube className="h-3 w-3 mr-1" />
            Canal YT
          </Button>
          <Button
            onClick={() => navigate('/help')}
            size="sm"
            variant="outline"
            className="bg-transparent border-[#39FF14] text-[#39FF14] rounded-none text-xs flex-shrink-0"
            data-testid="nav-help-button-mobile"
          >
            <HelpCircle className="h-3 w-3 mr-1" />
            Ajuda
          </Button>
          {isAdmin && (
            <Button
              onClick={() => navigate('/admin')}
              size="sm"
              variant="outline"
              className="bg-transparent border-[#FFB800] text-[#FFB800] rounded-none text-xs flex-shrink-0"
            >
              <Shield className="h-3 w-3 mr-1" />
              Admin
            </Button>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 relative z-10">
        {/* Motivational Quote of the Day - shows when all conditions met */}
        {todayWellness && (() => {
          const quote = getQuoteOfTheDay(user?._id || user?.email);
          return (
            <div className="mb-6 relative overflow-hidden bg-gradient-to-br from-[#00F0FF]/10 via-[#121212] to-[#0A0A0A] border border-[#00F0FF]/30 p-5 md:p-6" data-testid="motivational-quote-card">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#00F0FF]/5 blur-3xl rounded-full"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-3.5 w-3.5 text-[#00F0FF]" />
                  <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#00F0FF]">
                    Frase do Dia · Saúde Cardíaca
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <Quote className="h-5 w-5 text-[#00F0FF]/40 flex-shrink-0 mt-0.5" />
                  <p 
                    className="text-sm md:text-base leading-relaxed text-white font-medium"
                    data-testid="motivational-quote-text"
                  >
                    "{quote.text}"
                  </p>
                </div>
                
                <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <Heart className="h-3.5 w-3.5 text-[#00F0FF]" fill="#00F0FF" />
                    <p className="text-[10px] font-bold text-[#00F0FF] uppercase tracking-wider">
                      {quote.author}
                    </p>
                  </div>
                  <p className="text-[10px] text-[#39FF14] font-semibold hidden sm:block">
                    Coração forte = Atleta forte
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Monthly Feedback (athlete-facing) */}
        {monthlyFeedback?.has_data && (
          <div className="mb-6 bg-[#121212] border border-[#39FF14]/30 p-5 md:p-6" data-testid="monthly-feedback-card">
            <div className="flex items-start gap-3 mb-3">
              <Sparkles className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#39FF14] mb-1">Feedback Mensal Exclusivo</p>
                <h3 className="text-lg md:text-xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Sua evolução nos últimos 30 dias
                </h3>
              </div>
            </div>
            <p className="text-sm md:text-base text-white/90 leading-relaxed mb-4">
              {monthlyFeedback.summary}
            </p>
            {monthlyFeedback.highlights && monthlyFeedback.highlights.length > 0 && (
              <div className="bg-[#39FF14]/5 border border-[#39FF14]/20 p-3 mb-3">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#39FF14] mb-2">Benefícios do Protocolo TCS</p>
                <p className="text-[10px] text-[#A1A1AA] mb-2">Estes ganhos são consequência direta do seu acompanhamento contínuo no Protocolo TCS:</p>
                <ul className="space-y-1.5">
                  {monthlyFeedback.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs md:text-sm text-white">
                      <span className="text-[#39FF14] flex-shrink-0">▲</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {monthlyFeedback.improvements && monthlyFeedback.improvements.length > 0 && (
              <div className="bg-[#FFB800]/5 border border-[#FFB800]/20 p-3">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB800] mb-2">Pontos a Melhorar</p>
                <ul className="space-y-1.5">
                  {monthlyFeedback.improvements.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs md:text-sm text-white">
                      <span className="text-[#FFB800] flex-shrink-0">▲</span>
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Métricas do Ciclo — visíveis para o atleta */}
            {monthlyFeedback.metrics && (
              <div className="bg-[#0A0A0A] border border-white/10 p-3 mt-3" data-testid="athlete-cycle-metrics">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2">Métricas do Ciclo</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs md:text-sm">
                  <div><span className="text-[#A1A1AA]">Dias registrados:</span> <strong className="text-white">{monthlyFeedback.metrics.days_tracked}</strong></div>
                  <div><span className="text-[#A1A1AA]">Score médio:</span> <strong className="text-white">{monthlyFeedback.metrics.avg_score}/100</strong></div>
                  <div><span className="text-[#A1A1AA]">Sensação:</span> <strong className="text-white">{monthlyFeedback.metrics.avg_feeling}/10</strong></div>
                  <div><span className="text-[#A1A1AA]">Dores:</span> <strong className="text-white">{monthlyFeedback.metrics.avg_soreness}/10</strong></div>
                  <div><span className="text-[#A1A1AA]">Recuperação:</span> <strong className="text-white">{monthlyFeedback.metrics.avg_recovery}/10</strong></div>
                  <div><span className="text-[#A1A1AA]">Mental:</span> <strong className="text-white">{monthlyFeedback.metrics.avg_mental}/10</strong></div>
                  <div><span className="text-[#A1A1AA]">FC baixa:</span> <strong className="text-[#39FF14]">{monthlyFeedback.metrics.fc_baixa_percent}%</strong></div>
                  <div><span className="text-[#A1A1AA]">FC alta:</span> <strong className="text-[#FF3366]">{monthlyFeedback.metrics.fc_alta_percent}%</strong></div>
                  {monthlyFeedback.metrics.ctl_current !== null && monthlyFeedback.metrics.ctl_current !== undefined && (
                    <div><span className="text-[#A1A1AA]">CTL atual:</span> <strong className="text-[#00F0FF]">{monthlyFeedback.metrics.ctl_current}</strong>{monthlyFeedback.metrics.ctl_delta !== null && ` (${monthlyFeedback.metrics.ctl_delta >= 0 ? '+' : ''}${monthlyFeedback.metrics.ctl_delta})`}</div>
                  )}
                  {monthlyFeedback.metrics.atl_current !== null && monthlyFeedback.metrics.atl_current !== undefined && (
                    <div><span className="text-[#A1A1AA]">Fadiga (ATL):</span> <strong className="text-[#FFB800]">{monthlyFeedback.metrics.atl_current}</strong>{monthlyFeedback.metrics.atl_delta !== null && monthlyFeedback.metrics.atl_delta !== undefined && ` (${monthlyFeedback.metrics.atl_delta >= 0 ? '+' : ''}${monthlyFeedback.metrics.atl_delta})`}</div>
                  )}
                  {monthlyFeedback.metrics.tsb_current !== null && monthlyFeedback.metrics.tsb_current !== undefined && (
                    <div><span className="text-[#A1A1AA]">Forma (TSB):</span> <strong className={monthlyFeedback.metrics.tsb_current >= 0 ? 'text-[#39FF14]' : 'text-[#FF3366]'}>{monthlyFeedback.metrics.tsb_current >= 0 ? '+' : ''}{monthlyFeedback.metrics.tsb_current}</strong>{monthlyFeedback.metrics.tsb_delta !== null && monthlyFeedback.metrics.tsb_delta !== undefined && ` (${monthlyFeedback.metrics.tsb_delta >= 0 ? '+' : ''}${monthlyFeedback.metrics.tsb_delta})`}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Daily Status Banner */}
        {todayWellness?.daily_status && (
          <div 
            className="mb-6 border-2 p-6"
            style={{ 
              borderColor: todayWellness.daily_status.color,
              background: `linear-gradient(90deg, ${todayWellness.daily_status.color}20 0%, ${todayWellness.daily_status.color}05 100%)`
            }}
            data-testid="daily-status-banner"
          >
            <div className="flex items-start gap-4">
              <div 
                className="w-14 h-14 border-2 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: todayWellness.daily_status.color }}
              >
                <div 
                  className="w-6 h-6 animate-pulse"
                  style={{ backgroundColor: todayWellness.daily_status.color }}
                ></div>
              </div>
              <div className="flex-1">
                <p 
                  className="text-xs tracking-[0.3em] uppercase font-bold mb-1" 
                  style={{ color: todayWellness.daily_status.color }}
                >
                  Status do Dia · Score {todayWellness.daily_score}/100
                </p>
                <h2 
                  className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-2" 
                  style={{ fontFamily: 'Barlow Condensed, sans-serif', color: todayWellness.daily_status.color }}
                >
                  {todayWellness.daily_status.title}
                </h2>
                <p className="text-white">
                  {todayWellness.daily_status.message}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Alert / Subscription Status */}
        {paymentInfo && !paymentInfo.is_paid && (() => {
          const level = paymentInfo.status_level;
          const days = paymentInfo.days_until_due;
          const dueDate = new Date(paymentInfo.next_due_date).toLocaleDateString('pt-BR');
          
          // Style & copy per level
          let borderColor = '#FFB800', bg = 'rgba(255,184,0,0.1)', icon = <CreditCard className="h-5 w-5 flex-shrink-0 mt-0.5" style={{color: '#FFB800'}} />;
          let title = '';
          let healthMsg = '';
          let testid = 'payment-alert';
          
          if (level === 'overdue') {
            borderColor = '#FF3366'; bg = 'rgba(255,51,102,0.1)';
            icon = <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{color: '#FF3366'}} />;
            title = `Mensalidade VENCIDA há ${Math.abs(days)} ${Math.abs(days) === 1 ? 'dia' : 'dias'} · Acesso suspenso`;
            healthMsg = 'Seu monitoramento cardíaco foi pausado. Para a sua segurança, mantenha o acompanhamento ativo — dados diários reduzem risco de overtraining e problemas cardíacos. Envie o comprovante para reativar.';
            testid = 'payment-overdue-alert';
          } else if (level === 'due_today') {
            borderColor = '#FF3366'; bg = 'rgba(255,51,102,0.1)';
            icon = <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{color: '#FF3366'}} />;
            title = `Mensalidade vence HOJE (${dueDate}) · R$ ${paymentInfo.price?.toFixed(2).replace('.', ',')}`;
            healthMsg = 'Não interrompa seu monitoramento! O acompanhamento contínuo da sua saúde cardíaca e carga de treino é essencial para prevenir lesões e garantir seu desempenho.';
            testid = 'payment-due-today-alert';
          } else if (level === 'warning_1d') {
            borderColor = '#FFB800'; bg = 'rgba(255,184,0,0.15)';
            title = `Falta 1 DIA para o vencimento (${dueDate}) · R$ ${paymentInfo.price?.toFixed(2).replace('.', ',')}`;
            healthMsg = 'Manter o monitoramento ativo é parte fundamental do seu progresso. Seu coração e sua forma física agradecem a consistência. Garanta a renovação hoje.';
            testid = 'payment-warning-1d-alert';
          } else if (level === 'warning_2d') {
            borderColor = '#FFB800'; bg = 'rgba(255,184,0,0.1)';
            title = `Faltam 2 DIAS para o vencimento (${dueDate}) · R$ ${paymentInfo.price?.toFixed(2).replace('.', ',')}`;
            healthMsg = 'Acompanhamento constante de FC, CTL e bem-estar é o que separa atletas saudáveis de atletas em risco. Mantenha sua assinatura em dia e continue evoluindo com segurança.';
            testid = 'payment-warning-2d-alert';
          } else {
            // ok: não mostrar alerta para atletas em dia com >2 dias
            return null;
          }
          
          return (
            <div 
              className="mb-6 border p-4 flex items-start gap-3" 
              style={{ borderColor, backgroundColor: bg }}
              data-testid={testid}
            >
              {icon}
              <div className="flex-1">
                <p className="text-white font-semibold mb-2 text-sm md:text-base" style={{ color: borderColor }}>
                  {title}
                </p>
                <p className="text-xs md:text-sm text-white/90 mb-3 leading-relaxed flex items-start gap-2">
                  <Heart className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{color: borderColor}} fill={borderColor} />
                  <span>{healthMsg}</span>
                </p>
                <Button
                  onClick={() => navigate('/payment')}
                  size="sm"
                  className="font-bold uppercase rounded-none text-black"
                  style={{ backgroundColor: borderColor }}
                  data-testid="go-to-payment-button"
                >
                  {level === 'overdue' ? 'Reativar Acesso' : 'Ir para Mensalidade'}
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Wellness Alert */}
        {!todayWellness && (
          <div className="mb-6 bg-[#00F0FF]/10 border border-[#00F0FF] p-4 flex items-start gap-3" data-testid="wellness-alert">
            <AlertCircle className="h-5 w-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-semibold mb-2">Você ainda não respondeu o questionário de hoje</p>
              <Button
                onClick={() => navigate('/wellness')}
                className="bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase rounded-none h-10"
                data-testid="go-to-wellness-button"
              >
                Responder Agora
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Daily Score Widget */}
          <div className="md:col-span-2 lg:col-span-2 bg-[#121212] border border-white/10 p-8" data-testid="daily-score-widget">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardCheck className="h-5 w-5 text-[#00F0FF]" />
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">Score Diário</p>
            </div>
            {todayWellness ? (
              <>
                <div className="text-center mb-4">
                  <div 
                    className="text-8xl font-black"
                    style={{ 
                      fontFamily: 'Barlow Condensed, sans-serif',
                      color: getScoreColor(todayWellness.daily_score)
                    }}
                    data-testid="daily-score-value"
                  >
                    {todayWellness.daily_score}
                  </div>
                  <p className="text-[#A1A1AA] text-sm mt-2">de 100</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-[#0A0A0A] p-3 border border-white/5">
                    <p className="text-xs text-[#A1A1AA] mb-1 uppercase tracking-wider">Sensação Geral</p>
                    <p className="text-2xl font-bold text-[#00F0FF]">{todayWellness.como_se_sente}/10</p>
                  </div>
                  <div className="bg-[#0A0A0A] p-3 border border-white/5">
                    <p className="text-xs text-[#A1A1AA] mb-1 uppercase tracking-wider">Dores Musculares</p>
                    <p className="text-2xl font-bold text-[#00F0FF]">{todayWellness.dores_musculares}/10</p>
                  </div>
                  <div className="bg-[#0A0A0A] p-3 border border-white/5">
                    <p className="text-xs text-[#A1A1AA] mb-1 uppercase tracking-wider">Recuperação</p>
                    <p className="text-2xl font-bold text-[#00F0FF]">{todayWellness.recuperacao}/10</p>
                  </div>
                  <div className="bg-[#0A0A0A] p-3 border border-white/5">
                    <p className="text-xs text-[#A1A1AA] mb-1 uppercase tracking-wider">Saúde Mental</p>
                    <p className="text-2xl font-bold text-[#00F0FF]">{todayWellness.saude_mental}/10</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-[#A1A1AA] mb-4">Nenhum dado registrado hoje</p>
                <Button
                  onClick={() => navigate('/wellness')}
                  className="bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase rounded-none"
                >
                  Responder Questionário
                </Button>
              </div>
            )}
          </div>

          {/* CTL Widget */}
          <div className="md:col-span-1 lg:col-span-2 bg-[#121212] border border-white/10 p-8" data-testid="ctl-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-[#00F0FF]" />
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">CTL (Chronic Training Load)</p>
              </div>
              <Dialog open={ctlDialogOpen} onOpenChange={setCTLDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={ctlCanSubmit && !ctlCanSubmit.can_submit}
                    className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="open-ctl-dialog-button"
                    title={ctlCanSubmit && !ctlCanSubmit.can_submit
                      ? `Próxima janela: ${new Date(ctlCanSubmit.next_window_opens_at).toLocaleDateString('pt-BR')}`
                      : 'Registrar CTL deste ciclo'}
                  >
                    {ctlCanSubmit && !ctlCanSubmit.can_submit ? 'Registrado este ciclo' : 'Registrar CTL do Mês'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      Métricas do TrainingPeaks
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCTLSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">
                          Fadiga (ATL)
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={fadigaValue}
                          onChange={(e) => setFadigaValue(e.target.value)}
                          placeholder="Ex: 45.0"
                          className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-11"
                          data-testid="fadiga-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">
                          Condicion. (CTL) *
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={condicionamentoValue}
                          onChange={(e) => setCondicionamentoValue(e.target.value)}
                          required
                          placeholder="Ex: 55.0"
                          className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-11"
                          data-testid="condicionamento-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-2 block">
                          Forma (TSB)
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formaValue}
                          onChange={(e) => setFormaValue(e.target.value)}
                          placeholder="Ex: 10.0"
                          className="bg-[#0A0A0A] border-white/10 text-white focus:border-[#00F0FF] focus:ring-[#00F0FF] rounded-none h-11"
                          data-testid="forma-input"
                        />
                      </div>
                    </div>

                    <p className="text-xs text-[#A1A1AA]">
                      Todas as métricas estão no TrainingPeaks em "Performance Management Chart". Apenas Condicionamento (CTL) é obrigatório.
                      <br />
                      <span className="text-[#FFB800] font-semibold">Atenção: o CTL só pode ser registrado 1 vez por ciclo mensal. Após salvar, não é possível editar até a próxima renovação.</span>
                    </p>

                    <Button
                      type="submit"
                      disabled={submittingCTL}
                      className="w-full bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase rounded-none h-12"
                      data-testid="submit-ctl-button"
                    >
                      {submittingCTL ? 'Salvando...' : 'Salvar Métricas'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {latestCTL ? (
              <>
                <div className="text-center mb-4">
                  {(() => {
                    const prev = ctlHistory && ctlHistory.length >= 2 ? ctlHistory[ctlHistory.length - 2] : null;
                    const prevVal = prev ? (prev.condicionamento ?? prev.ctl_value) : null;
                    const currVal = latestCTL.condicionamento ?? latestCTL.ctl_value;
                    let trendIcon = null;
                    let trendLabel = null;
                    let trendColor = '#A1A1AA';
                    if (prevVal !== null && prevVal !== undefined && currVal !== null && currVal !== undefined) {
                      const diff = +(currVal - prevVal).toFixed(1);
                      if (diff > 0) {
                        trendIcon = '▲';
                        trendColor = '#39FF14';
                        trendLabel = `+${diff} vs mês anterior`;
                      } else if (diff < 0) {
                        trendIcon = '▼';
                        trendColor = '#FF3366';
                        trendLabel = `${diff} vs mês anterior`;
                      } else {
                        trendIcon = '—';
                        trendColor = '#A1A1AA';
                        trendLabel = 'Igual ao mês anterior';
                      }
                    }
                    return (
                      <>
                        <div className="flex items-center justify-center gap-3">
                          <div className="text-7xl font-black text-[#00F0FF]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="ctl-value">
                            {latestCTL.ctl_value}
                          </div>
                          {trendIcon && (
                            <div className="flex flex-col items-start" data-testid="ctl-trend">
                              <span className="text-3xl font-black leading-none" style={{ color: trendColor }}>
                                {trendIcon}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider mt-1" style={{ color: trendColor }}>
                                {trendLabel}
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                  <p className="text-[#A1A1AA] text-sm mt-2">
                    Última atualização: {new Date(latestCTL.date).toLocaleDateString('pt-BR')}
                  </p>
                  {ctlCanSubmit && !ctlCanSubmit.can_submit && (
                    <p className="text-[#FFB800] text-xs mt-1" data-testid="ctl-next-window">
                      Próxima janela de registro: {new Date(ctlCanSubmit.next_window_opens_at).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
                {(latestCTL.fadiga || latestCTL.condicionamento || latestCTL.forma) && (
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {latestCTL.fadiga !== null && latestCTL.fadiga !== undefined && (
                      <div className="bg-[#0A0A0A] p-2 border border-white/5 text-center">
                        <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Fadiga</p>
                        <p className="text-xl font-bold text-[#FFB800]">{latestCTL.fadiga}</p>
                      </div>
                    )}
                    {latestCTL.condicionamento !== null && latestCTL.condicionamento !== undefined && (
                      <div className="bg-[#0A0A0A] p-2 border border-white/5 text-center">
                        <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Condic.</p>
                        <p className="text-xl font-bold text-[#00F0FF]">{latestCTL.condicionamento}</p>
                      </div>
                    )}
                    {latestCTL.forma !== null && latestCTL.forma !== undefined && (
                      <div className="bg-[#0A0A0A] p-2 border border-white/5 text-center">
                        <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Forma</p>
                        <p className="text-xl font-bold text-[#39FF14]">{latestCTL.forma}</p>
                      </div>
                    )}
                  </div>
                )}
                {latestCTL.notes && (
                  <div className="bg-[#0A0A0A] p-3 border border-white/5 mt-4">
                    <p className="text-xs text-[#A1A1AA] mb-1 uppercase tracking-wider">Notas</p>
                    <p className="text-sm text-white">{latestCTL.notes}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-[#A1A1AA] mb-4">Nenhum CTL registrado ainda</p>
                <Button
                  onClick={() => setCTLDialogOpen(true)}
                  className="bg-[#00F0FF] hover:bg-[#4DFFFF] text-black font-bold uppercase rounded-none"
                >
                  Registrar Primeiro CTL
                </Button>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="md:col-span-3 lg:col-span-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/wellness')}
              className="bg-[#121212] border border-white/10 hover:border-[#00F0FF] transition-colors p-6 text-left group"
              data-testid="quick-wellness"
            >
              <Heart className="h-6 w-6 text-[#00F0FF] mb-3" />
              <h3 className="text-lg font-bold uppercase text-white group-hover:text-[#00F0FF] transition-colors">Questionário</h3>
              <p className="text-xs text-[#A1A1AA] mt-1">Registrar bem-estar diário</p>
            </button>
            <button
              onClick={() => navigate('/protocol')}
              className="bg-[#121212] border border-white/10 hover:border-[#00F0FF] transition-colors p-6 text-left group"
              data-testid="quick-protocol"
            >
              <FileText className="h-6 w-6 text-[#00F0FF] mb-3" />
              <h3 className="text-lg font-bold uppercase text-white group-hover:text-[#00F0FF] transition-colors">Protocolo TCS</h3>
              <p className="text-xs text-[#A1A1AA] mt-1">Treinos, nutrição e mais</p>
            </button>
            <button
              onClick={() => navigate('/payment')}
              className={`bg-[#121212] border transition-colors p-6 text-left group ${isOuro ? 'border-[#FFB800]/30 hover:border-[#FFB800]' : 'border-white/10 hover:border-[#00F0FF]'}`}
              data-testid="quick-payment"
            >
              <CreditCard className={`h-6 w-6 mb-3 ${isOuro ? 'text-[#FFB800]' : 'text-[#00F0FF]'}`} />
              <h3 className={`text-lg font-bold uppercase text-white transition-colors ${isOuro ? 'group-hover:text-[#FFB800]' : 'group-hover:text-[#00F0FF]'}`}>
                Mensalidade {isOuro ? 'Ouro' : 'Bronze'}
              </h3>
              <p className="text-xs text-[#A1A1AA] mt-1">R$ {paymentInfo?.price?.toFixed(2).replace('.', ',')} /mês</p>
            </button>
          </div>

          {/* Feedback Widget */}
          <div className="md:col-span-3 lg:col-span-4 bg-[#121212] border border-white/10 p-8" data-testid="feedback-widget">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="h-5 w-5 text-[#00F0FF]" />
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">Protocolo TCS · Análise</p>
            </div>

            {/* PROTOCOLO TCS — Benefício acumulado do ciclo */}
            {protocolBenefit && (
              <div className="mb-6 p-5 border" style={{ borderColor: `${protocolBenefit.color}50`, backgroundColor: `${protocolBenefit.color}08` }} data-testid="protocol-benefit-card">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: protocolBenefit.color }}>
                      Protocolo TCS · Benefício acumulado
                    </p>
                    <p className="text-[10px] text-[#A1A1AA] mt-1">
                      {protocolBenefit.color_label} · {protocolBenefit.days_responded} de {protocolBenefit.days_elapsed} dias respondidos
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl md:text-5xl font-black leading-none" style={{ color: protocolBenefit.color, fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="protocol-benefit-pct">
                      {protocolBenefit.benefit_pct}%
                    </p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="w-full h-3 bg-[#0A0A0A] border border-white/10 mb-3 overflow-hidden">
                  <div className="h-full transition-all duration-500" style={{ width: `${protocolBenefit.benefit_pct}%`, backgroundColor: protocolBenefit.color }}></div>
                </div>
                <p className="text-xs text-white/80 leading-relaxed">
                  Sua porcentagem sobe a cada dia que você responde o questionário com saúde — <strong style={{ color: protocolBenefit.color }}>FC de repouso baixa, boa recuperação e mente equilibrada</strong> aumentam mais. O benefício máximo (100%) só é atingido ao final do ciclo mensal de {protocolBenefit.days_in_cycle} dias.
                </p>
                <p className="text-[11px] text-[#A1A1AA] leading-relaxed mt-2">
                  Integramos <strong className="text-[#FC4C02]">Strava</strong> e <strong className="text-[#00F0FF]">TrainingPeaks</strong> para captura precisa das suas métricas de treino — combinadas ao questionário diário, traduzem em ganhos reais de saúde, performance e prevenção via Protocolo TCS.
                </p>
                {protocolBenefit.fc_baixa_percent > 0 && (
                  <div className="mt-3 flex items-center gap-3 text-xs">
                    <span className="text-[#A1A1AA]">FC repouso baixa:</span>
                    <span className="text-[#39FF14] font-bold">{protocolBenefit.fc_baixa_percent}% dos dias</span>
                  </div>
                )}
              </div>
            )}

            {feedback && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {feedback.daily_evolution && (
                    <div 
                      className="p-4 border" 
                      style={{ 
                        backgroundColor: `${feedback.daily_evolution.color}10`, 
                        borderColor: `${feedback.daily_evolution.color}40` 
                      }}
                      data-testid="daily-evolution-card"
                    >
                      <p className="text-xs text-[#A1A1AA] mb-2 uppercase tracking-wider">Evolução Dia</p>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl" aria-hidden="true">{feedback.daily_evolution.emoji}</span>
                        <p className="text-sm md:text-base font-bold leading-tight" style={{ color: feedback.daily_evolution.color }}>
                          {feedback.daily_evolution.label}
                        </p>
                      </div>
                      {feedback.daily_evolution.has_comparison && (
                        <p className="text-[10px] text-[#A1A1AA] mt-2 uppercase tracking-wider">
                          {feedback.daily_evolution.delta > 0 ? '+' : ''}{feedback.daily_evolution.delta} vs ontem
                        </p>
                      )}
                    </div>
                  )}
                  <div className="bg-[#0A0A0A] p-4 border border-white/5">
                    <p className="text-xs text-[#A1A1AA] mb-2 uppercase tracking-wider">Status CTL</p>
                    <p className="text-xl font-bold text-white">{feedback.ctl_status}</p>
                  </div>
                  <div className="bg-[#0A0A0A] p-4 border border-white/5">
                    <p className="text-xs text-[#A1A1AA] mb-2 uppercase tracking-wider">Status Bem-Estar</p>
                    <p className="text-xl font-bold text-white">{feedback.wellness_status}</p>
                  </div>
                  <div className="bg-[#0A0A0A] p-4 border border-white/5">
                    <p className="text-xs text-[#A1A1AA] mb-2 uppercase tracking-wider">Score Geral</p>
                    <p className="text-xl font-bold" style={{ color: getScoreColor(feedback.overall_score) }} data-testid="overall-score">
                      {feedback.overall_score}/100
                    </p>
                  </div>
                </div>

                <div className="bg-[#0A0A0A] p-6 border border-white/5">
                  <h3 className="text-lg font-bold uppercase tracking-tight text-white mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Recomendações
                  </h3>
                  <ul className="space-y-3">
                    {feedback.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-3" data-testid={`recommendation-${index}`}>
                        <div className="w-1.5 h-1.5 bg-[#00F0FF] mt-2 flex-shrink-0"></div>
                        <p className="text-white">{rec}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
